import useUserBranches from "@/core/auth/hooks/useUserBranches";
import { useSalesChannelOptions } from "@/features/branch-sales-channel/usecases/useSalesChannelOptions";
import {
	type AutocompleteFetcher,
	toast,
	useAuth,
	useDialogController,
} from "@k3-tech/react-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	UnavailableItemDialog,
	type UnavailableItemFormValues,
} from "../components/UnavailableItemDialog";
import { UnavailableItemTable } from "../components/UnavailableItemTable";
import {
	type BranchProductSalesChannelItemRow,
	useBranchProductSalesChannelItemList,
} from "../usecases/useBranchProductSalesChannelItemList";
import {
	BRANCH_PRODUCT_ITEM_TYPE_EXCLUDE,
	useBranchProductSalesChannelItemMutations,
} from "../usecases/useBranchProductSalesChannelItemMutations";
import { useMenuGroupBindings } from "../usecases/useMenuGroupBindings";
import { useMenuGroupBranchAutocomplete } from "../usecases/useMenuGroupBranchAutocomplete";
import { useMenuGroupProductAutocomplete } from "../usecases/useMenuGroupProductAutocomplete";

export interface UnavailableItemContainerProps {
	groupID: string;
}

export const UnavailableItemContainer = ({
	groupID,
}: UnavailableItemContainerProps) => {
	const { confirm } = useDialogController();
	const { session } = useAuth();
	const { isAllBranches } = useUserBranches();
	const { nameByID: channelNameByID } = useSalesChannelOptions();
	const { channelIDs, branchesByChannelID } = useMenuGroupBindings(groupID);
	const [pickedChannelID, setPickedChannelID] = useState("");
	const [addOpen, setAddOpen] = useState(false);

	const channelOptions = useMemo(
		() =>
			channelIDs.map((channelID) => ({
				value: channelID,
				label: channelNameByID.get(channelID) ?? channelID,
			})),
		[channelIDs, channelNameByID],
	);

	// Channel options arrive async, so fall back to the first until one is picked.
	const channelID = pickedChannelID || channelOptions[0]?.value || "";

	// Preselect the user's own branch, but only when this group covers it.
	const defaultBranch = useMemo(() => {
		const defaultBranchID = session?.meta?.defaultBranchID as
			| string
			| undefined;
		if (!defaultBranchID) return null;
		return branchesByChannelID.get(channelID)?.get(defaultBranchID) ?? null;
	}, [branchesByChannelID, channelID, session?.meta?.defaultBranchID]);

	const defaultBranchOption = useMemo(() => {
		if (!defaultBranch) return null;
		return {
			value: defaultBranch.branchID,
			label: defaultBranch.branchCode
				? `${defaultBranch.branchName} (${defaultBranch.branchCode})`
				: defaultBranch.branchName,
		};
	}, [defaultBranch]);

	const branchOptions = useMemo(() => {
		const branchMap = branchesByChannelID.get(channelID);
		if (!branchMap) return [];
		return Array.from(branchMap.values()).map((b) => ({
			value: b.branchID,
			label: b.branchCode ? `${b.branchName} (${b.branchCode})` : b.branchName,
		}));
	}, [branchesByChannelID, channelID]);

	const [formFilterValues, setFormFilterValues] = useState<
		Record<string, unknown>
	>(() => ({
		branchID: defaultBranch?.branchID,
		salesChannelID: channelID,
	}));

	const prevDefaultBranchIDRef = useRef<string | undefined>(
		defaultBranch?.branchID,
	);

	const initialFilters = useMemo(() => {
		const init: Record<string, unknown> = {};
		if (defaultBranch?.branchID) {
			init.branchID = defaultBranch.branchID;
		}
		if (channelID) {
			init.salesChannelID = channelID;
		}
		return init;
	}, [defaultBranch?.branchID, channelID]);

	const {
		items,
		rowCount,
		fetching,
		load,
		paginationState,
		setPagination,
		setFilters,
	} = useBranchProductSalesChannelItemList(groupID, undefined, initialFilters);

	const { disable, restore } = useBranchProductSalesChannelItemMutations();
	const { load: loadBranch } = useMenuGroupBranchAutocomplete(
		groupID,
		channelID,
	);
	const { load: loadProduct } = useMenuGroupProductAutocomplete(groupID);

	const branchAutocompleteFetcher: AutocompleteFetcher = useCallback(
		(params) => loadBranch(params.page, params.pageSize, params.search),
		[loadBranch],
	);

	const productAutocompleteFetcher: AutocompleteFetcher = useCallback(
		(params) => loadProduct(params.search),
		[loadProduct],
	);

	// Sync when defaultBranch changes (e.g. switcher)
	useEffect(() => {
		const nextBranchID = defaultBranch?.branchID;
		if (nextBranchID && nextBranchID !== prevDefaultBranchIDRef.current) {
			prevDefaultBranchIDRef.current = nextBranchID;
			setFormFilterValues((prev: Record<string, unknown>) => {
				const nextValues: Record<string, unknown> = {
					...prev,
					branchID: nextBranchID,
					salesChannelID: channelID,
				};
				setFilters({
					branchID: nextBranchID,
					salesChannelID: channelID || null,
					type: nextValues.type || null,
				});
				return nextValues;
			});
		}
	}, [defaultBranch?.branchID, channelID, setFilters]);

	const onFormFilterChange = useCallback(
		(values: Record<string, unknown>) => {
			const nextChannel = (values.salesChannelID as string) || channelID;
			setPickedChannelID(nextChannel);
			const effectiveBranchID =
				(values.branchID as string) ||
				(!isAllBranches ? defaultBranch?.branchID || null : null);
			const nextValues = {
				...values,
				branchID: effectiveBranchID,
			};
			setFormFilterValues(nextValues);
			setFilters({
				branchID: effectiveBranchID,
				salesChannelID: nextChannel || null,
				type: values.type || null,
			});
		},
		[channelID, setFilters, isAllBranches, defaultBranch?.branchID],
	);

	const onMarkUnavailable = async (values: UnavailableItemFormValues) => {
		const result = await disable({
			branchID: values.branchID,
			productID: values.productID,
			salesChannelID: channelID,
			salesChannelCategoryGroupID: groupID,
			type: BRANCH_PRODUCT_ITEM_TYPE_EXCLUDE,
			note: values.note,
		});

		if (result.error) {
			toast.error(result.error.message);
			throw result.error;
		}

		toast.success("Item marked unavailable");
		setAddOpen(false);
		load();
	};

	const onRestore = async (record: BranchProductSalesChannelItemRow) => {
		const ok = await confirm({
			title: `Restore ${record.productName}?`,
			description: "The item returns to the channel menu.",
			confirmText: "Restore",
			cancelText: "Cancel",
		});
		if (!ok) return;

		const result = await restore({
			branchID: record.branchID,
			productID: record.productID,
			salesChannelID: record.salesChannelID,
			type: record.type,
		});

		if (result.error) {
			toast.error(result.error.message);
			return;
		}

		toast.success("Item restored");
		load();
	};

	return (
		<>
			<UnavailableItemTable
				data={items}
				loading={fetching}
				onRefresh={load}
				onFormFilterChange={onFormFilterChange}
				formFilterValues={formFilterValues}
				paginationState={paginationState}
				onPaginationChange={setPagination}
				rowCount={rowCount}
				branchAutocompleteFetcher={branchAutocompleteFetcher}
				branchOptions={branchOptions}
				channelOptions={channelOptions}
				channelNameByID={channelNameByID}
				selectedChannelID={channelID}
				onRestore={onRestore}
				onAdd={() => setAddOpen(true)}
			/>

			<UnavailableItemDialog
				open={addOpen}
				onOpenChange={setAddOpen}
				branchFetcher={branchAutocompleteFetcher}
				productFetcher={productAutocompleteFetcher}
				defaultBranchOption={defaultBranchOption}
				onSubmit={onMarkUnavailable}
			/>
		</>
	);
};
